/**
 * Client-side {{lead variable}} substitution for the web-chat opening message.
 *
 * The dashboard-authored opening (`ai_config.webchat.initial_message`, returned
 * by GET /api/public/chat/config) is a RAW template like
 * "Hola {{first_name}}, ¿en qué te ayudamos?". The widget renders it here before
 * painting the greeting bubble.
 *
 * This MUST stay in lockstep with the server's renderer
 * (nexor-node-api `src/shared/render-lead-template.js`) so the visitor sees the
 * same greeting the agent is told was shown. Same tokens, aliases, and tidy-up.
 *
 * Tokens are case-insensitive and tolerate inner spaces: {{name}}, {{ Name }}.
 * Unknown tokens are left untouched. Missing/empty values resolve to "" and the
 * result is tidied so "Hola {{first_name}}," with no name reads "Hola,".
 */

export interface TemplateLead {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

// Friendly aliases → canonical lead field. Keep in sync with the server's
// ALIASES map and the dashboard's LEAD_TEMPLATE_VARIABLES.
const ALIASES: Record<string, keyof TemplateLead | "full_name"> = {
  first_name: "first_name",
  name: "first_name",
  lead_first_name: "first_name",
  last_name: "last_name",
  lastname: "last_name",
  lead_last_name: "last_name",
  full_name: "full_name",
  fullname: "full_name",
  email: "email",
  phone: "phone",
};

function clean(v: unknown): string {
  return v == null ? "" : String(v).trim();
}

function leadValue(lead: TemplateLead | null | undefined, canonical: string): string {
  if (!lead) return "";
  switch (canonical) {
    case "first_name":
      return clean(lead.first_name);
    case "last_name":
      return clean(lead.last_name);
    case "full_name":
      return [clean(lead.first_name), clean(lead.last_name)].filter(Boolean).join(" ");
    case "email":
      return clean(lead.email);
    case "phone":
      return clean(lead.phone);
    default:
      return "";
  }
}

/**
 * Render a template against a lead. Returns "" for empty/non-string input.
 */
export function renderLeadTemplate(
  template: string | null | undefined,
  lead: TemplateLead | null | undefined,
): string {
  if (typeof template !== "string" || !template) return "";

  const replaced = template.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (match, raw: string) => {
    const canonical = ALIASES[raw.toLowerCase()];
    if (!canonical) return match; // leave unknown tokens as-authored
    return leadValue(lead, canonical);
  });

  // Tidy leftovers from empty substitutions (mirrors the server):
  //   "Hola ,"  → "Hola,"   ·   "Hola  Ana" → "Hola Ana"
  //   ", hola"  → "hola"     ·   trim trailing space per line, then overall.
  return replaced
    .replace(/[ \t]+([,.!?;:])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/^[ \t]*[,;:][ \t]*/gm, "")
    .replace(/[ \t]+$/gm, "")
    .trim();
}
